import { BetDetails } from '../models/MyBetModel.tsx';
import { Odd } from '../models/Odd_response_model.tsx';

export type PropsConflictReason =
    | 'same_market'
    | 'same_statistic_threshold'
    | 'implies'
    | 'includes'
    | 'opposite';

export type PropsConflictSubject = {
    id: string;
    fixtureId?: string | null;
    marketId?: string | null;
    market?: string | null;
    playerId?: string | null;
    teamId?: string | null;
    selectionLine?: string | null;
    points?: number | null;
    selection?: string | null;
    name?: string | null;
    groupingKey?: string | null;
};

export const PROPS_CONFLICT_MESSAGES: Record<PropsConflictReason, string> = {
    same_market: 'Same market already selected.',
    same_statistic_threshold: 'Another threshold of the same statistic is already selected.',
    implies: 'This outcome is already implied by another selection.',
    includes: 'This market already includes another selected market.',
    opposite: 'Opposite side of an existing market cannot be combined.',
};

const normalize = (value?: string | null) => String(value ?? '').trim().toLowerCase();

const marketFamily = (marketId?: string | null, market?: string | null) => {
    const id = normalize(marketId)
        .replace(/_alternate$/, '')
        .replace(/_alt$/, '')
        .replace(/_extended$/, '');
    if (id) return id;
    return normalize(market)
        .replace(/\s+alternate$/, '')
        .replace(/\s+alt$/, '');
};

const lineSide = (line?: string | null): 'over' | 'under' | 'yes' | 'no' | 'other' => {
    const value = normalize(line);
    if (value.startsWith('over')) return 'over';
    if (value.startsWith('under')) return 'under';
    if (value === 'yes') return 'yes';
    if (value === 'no') return 'no';
    return 'other';
};

const outcomeSide = (item: PropsConflictSubject) => {
    const fromLine = lineSide(item.selectionLine);
    if (fromLine !== 'other') return fromLine;
    const fromSelection = lineSide(item.selection);
    if (fromSelection !== 'other') return fromSelection;
    return lineSide(item.name);
};

const subjectKey = (item: PropsConflictSubject): string => {
    if (item.playerId != null && String(item.playerId) !== '') {
        return `player:${item.playerId}`;
    }

    const family = marketFamily(item.marketId, item.market);
    const marketName = normalize(item.market);

    // Game-level markets (moneyline / spread / game total): one subject for the fixture
    // so opposite sides conflict. Do NOT key by team via grouping_key.
    if (
        family.includes('moneyline') ||
        marketName.includes('moneyline') ||
        family.includes('point_spread') ||
        family.includes('run_line') ||
        family.includes('puck_line') ||
        ((family.includes('total') || marketName.includes('total')) &&
            !family.includes('team_total') &&
            !marketName.includes('team total') &&
            !family.includes('player') &&
            !marketName.includes('player'))
    ) {
        return 'game';
    }

    const isTeamMarket =
        family.includes('team_total') ||
        marketName.includes('team total');

    if (isTeamMarket && item.teamId != null && String(item.teamId) !== '') {
        return `team:${item.teamId}`;
    }

    // Yes/No (or similar) game props must share one subject so Yes blocks No.
    const side = outcomeSide(item);
    if (side === 'yes' || side === 'no') {
        return 'game';
    }

    // Player props: player markets, or grouping keys like "Player Name:line"
    const isPlayerProp =
        family.includes('player') ||
        marketName.includes('player') ||
        Boolean(
            item.groupingKey &&
            item.groupingKey.includes(':') &&
            !normalize(item.groupingKey).startsWith('default')
        );

    if (isPlayerProp) {
        const playerLabel =
            normalize(item.selection) ||
            (item.groupingKey?.includes(':')
                ? normalize(item.groupingKey.split(':')[0])
                : normalize(item.groupingKey)) ||
            normalize(item.name) ||
            item.id;
        return `player:${playerLabel}`;
    }

    // Two-way team markets (e.g. First Team To Score): one subject for the fixture.
    return 'game';
};

const sameFixture = (a: PropsConflictSubject, b: PropsConflictSubject) => {
    if (!a.fixtureId || !b.fixtureId) return true;
    return String(a.fixtureId) === String(b.fixtureId);
};

const sameStatisticFamily = (a: PropsConflictSubject, b: PropsConflictSubject) => {
    if (!sameFixture(a, b)) return false;
    if (subjectKey(a) !== subjectKey(b)) return false;

    const familyA = marketFamily(a.marketId, a.market);
    const familyB = marketFamily(b.marketId, b.market);
    if (!familyA || !familyB) return false;
    return familyA === familyB;
};

const isExactSameOutcome = (a: PropsConflictSubject, b: PropsConflictSubject) => {
    if (a.id && b.id && a.id === b.id) return true;
    if (!sameStatisticFamily(a, b)) return false;

    return (
        lineSide(a.selectionLine) === lineSide(b.selectionLine) &&
        Number(a.points ?? NaN) === Number(b.points ?? NaN) &&
        normalize(a.selection) === normalize(b.selection) &&
        normalize(a.name) === normalize(b.name) &&
        String(a.teamId ?? '') === String(b.teamId ?? '')
    );
};

const isOppositeSide = (a: PropsConflictSubject, b: PropsConflictSubject) => {
    if (!sameStatisticFamily(a, b)) return false;

    const sideA = outcomeSide(a);
    const sideB = outcomeSide(b);

    if (
        (sideA === 'over' && sideB === 'under') ||
        (sideA === 'under' && sideB === 'over') ||
        (sideA === 'yes' && sideB === 'no') ||
        (sideA === 'no' && sideB === 'yes')
    ) {
        return true;
    }

    // Two-way markets (moneyline / pick sides / first team to score) without over/under
    if (sideA === 'other' && sideB === 'other') {
        if (a.teamId && b.teamId && String(a.teamId) !== String(b.teamId)) return true;
        if (normalize(a.selection) && normalize(b.selection) && normalize(a.selection) !== normalize(b.selection)) {
            return true;
        }
        if (normalize(a.name) && normalize(b.name) && normalize(a.name) !== normalize(b.name)) {
            return true;
        }
    }

    return false;
};

const isDifferentThreshold = (a: PropsConflictSubject, b: PropsConflictSubject) => {
    if (!sameStatisticFamily(a, b)) return false;
    if (isOppositeSide(a, b)) return false;
    if (a.points == null || b.points == null) return false;
    return Number(a.points) !== Number(b.points);
};

const isImpliedOrIncluded = (a: PropsConflictSubject, b: PropsConflictSubject): PropsConflictReason | null => {
    if (!sameStatisticFamily(a, b)) return null;

    const sideA = outcomeSide(a);
    const sideB = outcomeSide(b);
    if (sideA !== sideB) return null;
    if (a.points == null || b.points == null) return null;
    if (Number(a.points) === Number(b.points)) return null;

    // Over 2.5 implies Over 1.5; Under 1.5 implies Under 2.5
    if (sideA === 'over' || sideA === 'under') {
        return Number(a.points) > Number(b.points)
            ? (sideA === 'over' ? 'implies' : 'includes')
            : (sideA === 'over' ? 'includes' : 'implies');
    }

    return 'includes';
};

export function toPropsConflictSubjectFromOdd(odd: Odd, fixtureId?: string | null): PropsConflictSubject {
    return {
        id: odd.id,
        fixtureId,
        marketId: odd.market_id,
        market: odd.market,
        playerId: odd.player_id,
        teamId: odd.team_id,
        selectionLine: odd.selection_line,
        points: odd.points,
        selection: odd.selection,
        name: odd.name,
        groupingKey: odd.grouping_key,
    };
}

export function toPropsConflictSubjectFromBet(bet: BetDetails): PropsConflictSubject {
    const odd = bet.odd;
    return {
        id: String(bet.id_selection ?? odd?.id ?? ''),
        fixtureId: bet.fixture_id,
        marketId: odd?.market_id ?? bet.marketName,
        market: odd?.market ?? bet.marketName,
        playerId: odd?.player_id,
        teamId: odd?.team_id ?? undefined,
        selectionLine: odd?.selection_line ?? bet.selectionType,
        points: odd?.points,
        selection: odd?.selection ?? bet.betName,
        name: odd?.name ?? bet.betName,
        groupingKey: odd?.grouping_key,
    };
}

/**
 * Props / same-game combination rules:
 * 1 same market → reject
 * 2 another threshold of same statistic → reject
 * 3 one outcome implies the other → reject
 * 4 market already includes another selected market → reject
 * 5 opposite side of existing market → reject
 *
 * One pick per subject (game / team / player) within the same market_id —
 * e.g. Yes blocks No; Phillies blocks Nationals on First Team To Score;
 * same player cannot combine two lines of the same prop.
 */
export function getPropsConflict(
    candidate: PropsConflictSubject,
    existing: PropsConflictSubject
): PropsConflictReason | null {
    if (!candidate?.id || !existing?.id) return null;
    if (candidate.id === existing.id) return null;

    // Strict: one selection per market_id + subject (player / team / game)
    if (
        sameFixture(candidate, existing) &&
        String(candidate.marketId ?? '') !== '' &&
        String(candidate.marketId) === String(existing.marketId) &&
        subjectKey(candidate) === subjectKey(existing)
    ) {
        return 'same_market';
    }

    if (isExactSameOutcome(candidate, existing)) return 'same_market';
    if (isOppositeSide(candidate, existing)) return 'opposite';
    if (isDifferentThreshold(candidate, existing)) return 'same_statistic_threshold';

    const implied = isImpliedOrIncluded(candidate, existing);
    if (implied) return implied;

    if (sameStatisticFamily(candidate, existing)) return 'same_market';

    return null;
}

export function findPropsConflict(
    candidate: PropsConflictSubject,
    selected: PropsConflictSubject[]
): { existing: PropsConflictSubject; reason: PropsConflictReason } | null {
    for (const existing of selected) {
        const reason = getPropsConflict(candidate, existing);
        if (reason) return { existing, reason };
    }
    return null;
}

export function isPropsOddBlockedByBets(
    odd: Odd,
    bets: Array<BetDetails | { id_selection?: string; odd?: Odd; fixture_id?: string }>,
    fixtureId?: string | null
): boolean {
    const candidate = toPropsConflictSubjectFromOdd(odd, fixtureId);
    const selected = bets
        .filter(b => b?.id_selection || b?.odd?.id)
        .map(b => ('fixture_id' in b || 'marketName' in b)
            ? toPropsConflictSubjectFromBet(b as BetDetails)
            : toPropsConflictSubjectFromOdd(b.odd as Odd, (b as any).fixture_id));

    const own = selected.find(s => s.id === candidate.id);
    if (own) return false;

    return findPropsConflict(candidate, selected) != null;
}
